/**
 * 知识图谱面板主组件
 * 从 MemoryService 迁移，适配 Axiom 架构
 */

import { useEffect, useState, useCallback } from 'react'
import { useGraphStore, type GraphNode } from '../../store/graphStore'
import { useChatStore } from '../../store/chatStore'
import GraphHeader from './GraphHeader'
import GraphLegend from './GraphLegend'
import NodeTooltip from './NodeTooltip'
import GraphCanvas2D from './GraphCanvas2D'
import GraphCanvas3D from './GraphCanvas3D'
import { Loader2, AlertCircle } from 'lucide-react'

interface GraphPanelProps {
  isOpen: boolean
  onClose: () => void
  viewingUserId?: string // 要显示的用户ID（由外部决定，不在组件内部做 fallback）
  projectId?: string // 项目ID，用于过滤记忆数据
}

export default function GraphPanel({ isOpen, onClose, viewingUserId, projectId }: GraphPanelProps) {
  const { isExtracting, extractionResult } = useChatStore()
  
  // 直接使用外部传入的 userId，不在组件内部做 fallback
  // 外部（Chat.tsx）应该传入 viewingUserId || currentUserId
  const targetUserId = viewingUserId
  const {
    isLoading,
    isInitialized,
    error,
    actualMode,
    loadInitial,
    refresh,
    getNodesArray,
    getEdgesArray,
    hasMoreEntities,
    hasMoreRelations,
    loadMore,
  } = useGraphStore()
  
  // Tooltip 状态
  const [tooltipNode, setTooltipNode] = useState<GraphNode | null>(null)
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })
  const [tooltipVisible, setTooltipVisible] = useState(false)
  
  // 选中节点高亮
  const [, setSelectedNode] = useState<string | null>(null)
  
  // 加载数据
  useEffect(() => {
    if (isOpen && targetUserId && !isInitialized) {
      loadInitial(targetUserId, projectId)
    }
  }, [isOpen, targetUserId, projectId, isInitialized, loadInitial])
  
  // 当查看的用户或项目变化时，重新加载数据
  useEffect(() => {
    if (isOpen && targetUserId && isInitialized) {
      const { userId: currentUserId, projectId: currentProjectId } = useGraphStore.getState()
      if (currentUserId !== targetUserId || currentProjectId !== projectId) {
        loadInitial(targetUserId, projectId)
      }
    }
  }, [isOpen, targetUserId, projectId, isInitialized, loadInitial])
  
  // 提取完成后自动刷新图谱
  useEffect(() => {
    if (isOpen && extractionResult && !isExtracting && isInitialized) {
      const timer = setTimeout(() => {
        refresh()
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [isOpen, extractionResult, isExtracting, isInitialized, refresh])
  
  // 处理节点 hover
  const handleNodeHover = useCallback((node: GraphNode | null, position: { x: number; y: number }) => {
    setTooltipNode(node)
    setTooltipPosition(position)
    setTooltipVisible(!!node)
  }, [])
  
  // 处理节点点击
  const handleNodeClick = useCallback((node: GraphNode) => {
    setSelectedNode(prev => prev === node.id ? null : node.id)
  }, [])
  
  // 处理刷新
  const handleRefresh = useCallback(() => {
    refresh()
  }, [refresh])
  
  // 加载更多
  const handleLoadMore = useCallback(() => {
    if (hasMoreEntities || hasMoreRelations) {
      loadMore()
    }
  }, [hasMoreEntities, hasMoreRelations, loadMore])
  
  if (!isOpen) return null
  
  const nodesArray = getNodesArray()
  const edgesArray = getEdgesArray()
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0, 0, 0, 0.6)' }}
    >
      <div 
        className="relative w-[90vw] h-[85vh] max-w-6xl rounded-2xl overflow-hidden shadow-2xl animate-scale-in bg-card border border-border"
      >
        {/* 头部 */}
        <GraphHeader onClose={onClose} onRefresh={handleRefresh} />
        
        {/* 主内容区 */}
        <div className="relative" style={{ height: 'calc(100% - 73px)' }}>
          {/* 加载状态 */}
          {isLoading && !isInitialized && (
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <div className="text-center">
                <Loader2 
                  className="w-10 h-10 animate-spin mx-auto mb-3 text-blue-500" 
                />
                <p className="text-slate-400">正在加载知识图谱...</p>
              </div>
            </div>
          )}
          
          {/* 错误状态 */}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <div className="text-center">
                <AlertCircle 
                  className="w-10 h-10 mx-auto mb-3 text-red-500" 
                />
                <p className="text-red-400 mb-3">{error}</p>
                <button
                  onClick={handleRefresh}
                  className="px-4 py-2 rounded-lg text-white text-sm bg-blue-500 hover:bg-blue-600"
                >
                  重试
                </button>
              </div>
            </div>
          )}
          
          {/* 空状态 */}
          {isInitialized && nodesArray.length === 0 && !error && (
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <div className="text-center">
                <div className="text-5xl mb-4">🕸️</div>
                <p className="text-slate-400 mb-2">
                  暂无知识图谱数据
                </p>
                <p className="text-sm text-slate-500">
                  开始对话后，系统会自动提取实体和关系
                </p>
              </div>
            </div>
          )}
          
          {/* 图谱渲染 */}
          {isInitialized && nodesArray.length > 0 && (
            <>
              {/* 根据模式选择渲染器 */}
              {actualMode === '3d' ? (
                <GraphCanvas3D
                  nodes={nodesArray}
                  edges={edgesArray}
                  onNodeHover={handleNodeHover}
                  onNodeClick={handleNodeClick}
                  className="w-full h-full"
                />
              ) : (
                <GraphCanvas2D
                  nodes={nodesArray}
                  edges={edgesArray}
                  onNodeHover={handleNodeHover}
                  onNodeClick={handleNodeClick}
                  className="w-full h-full"
                />
              )}
              
              {/* 图例 */}
              <GraphLegend />
              
              {/* 加载更多按钮 */}
              {(hasMoreEntities || hasMoreRelations) && (
                <button
                  onClick={handleLoadMore}
                  disabled={isLoading}
                  className="absolute bottom-4 right-4 px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-70"
                >
                  {isLoading ? '加载中...' : '加载更多'}
                </button>
              )}
            </>
          )}
          
          {/* Tooltip */}
          <NodeTooltip
            node={tooltipNode}
            position={tooltipPosition}
            visible={tooltipVisible}
          />
        </div>
      </div>
    </div>
  )
}

